
import { User, UserRole } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserPlus, Send, Trash2, ArrowUpDown } from "lucide-react";
import { UserRoleBadge } from "./UserRoleBadge";
import { useState } from "react";
import { format } from "date-fns";

interface UsersTableProps {
  users: User[];
  onRemoveRole: (userId: string, role: UserRole) => Promise<void>;
  onOpenAddRoleDialog: (user: User) => void;
  onOpenTestNotificationDialog: (user: User) => void;
  onOpenDeleteUserDialog: (user: User) => void;
  isLastAdminUser: (userId: string) => boolean;
}

type SortField = 'name' | 'email' | 'lastLogin';
type SortDirection = 'asc' | 'desc';

export function UsersTable({ 
  users, 
  onRemoveRole, 
  onOpenAddRoleDialog, 
  onOpenTestNotificationDialog,
  onOpenDeleteUserDialog,
  isLastAdminUser
}: UsersTableProps) {
  // Helper function to check if user has admin role
  const hasAdminRole = (user: User): boolean => {
    return Array.isArray(user.roles) && user.roles.includes("ADMIN");
  };

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    if (sortField === 'name') {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return sortDirection === 'asc' 
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    } else if (sortField === 'email') {
      const emailA = (a.email || '').toLowerCase();
      const emailB = (b.email || '').toLowerCase();
      return sortDirection === 'asc'
        ? emailA.localeCompare(emailB)
        : emailB.localeCompare(emailA);
    } else if (sortField === 'lastLogin') {
      const dateA = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
      const dateB = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
      return sortDirection === 'asc'
        ? dateA - dateB
        : dateB - dateA;
    }
    return 0;
  });

  // Toggle sort
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Format date with fallback
  const formatDate = (date?: string | null) => {
    if (!date) return "Never";
    try {
      return format(new Date(date), 'PPP p');
    } catch (e) {
      return "Invalid date";
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => toggleSort('name')}
                className="h-8 px-2 flex items-center -ml-3 font-medium text-muted-foreground"
              >
                Name
                <ArrowUpDown className="ml-1 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => toggleSort('email')}
                className="h-8 px-2 flex items-center -ml-3 font-medium text-muted-foreground"
              >
                Email
                <ArrowUpDown className="ml-1 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button 
                variant="ghost" 
                onClick={() => toggleSort('lastLogin')}
                className="h-8 px-2 flex items-center -ml-3 font-medium text-muted-foreground"
              >
                Last Login
                <ArrowUpDown className="ml-1 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>Roles</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedUsers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center h-24">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            sortedUsers.map(user => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name || "N/A"}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{formatDate(user.lastLogin)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles?.map(role => (
                      <UserRoleBadge 
                        key={role} 
                        role={role}
                        onRemove={
                          role !== "USER" && 
                          !(role === "ADMIN" && isLastAdminUser(user.id)) ? 
                            (role) => onRemoveRole(user.id, role) : 
                            undefined
                        }
                      />
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex gap-1"
                      onClick={() => onOpenAddRoleDialog(user)}
                    >
                      <UserPlus className="h-4 w-4" />
                      Add Role
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex gap-1"
                      onClick={() => onOpenTestNotificationDialog(user)}
                    >
                      <Send className="h-4 w-4" />
                      Test Notification
                    </Button>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onOpenDeleteUserDialog(user)}
                      disabled={hasAdminRole(user) && isLastAdminUser(user.id)}
                      title={hasAdminRole(user) && isLastAdminUser(user.id) ? 
                        "Cannot delete the last admin user" : "Delete user"}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
